/**
 * Picos de atendimento: heatmap 7×24 em CSS grid — intensidade teal
 * proporcional à contagem de tickets criados, tooltip nativo por célula —
 * mais a coluna de totais por dia da semana.
 */
import type { PeaksReport } from '@erp/shared';
import { WEEKDAY_LABELS } from './report-format';
import type { CsvTable } from './report-views';

/** Teal primário (#0CC4B6) com alpha pela intensidade; zero fica quase invisível. */
function cellColor(count: number, max: number): string {
  if (count === 0) return 'rgba(12, 196, 182, 0.06)';
  return `rgba(12, 196, 182, ${(0.2 + 0.8 * (count / max)).toFixed(3)})`;
}

export function PeaksView({ data }: { data: PeaksReport }) {
  const max = Math.max(1, ...data.matrix.map((row) => Math.max(...row)));
  return (
    <div className="overflow-x-auto p-4">
      <div
        className="grid min-w-[720px] items-center gap-px"
        style={{ gridTemplateColumns: 'auto repeat(24, minmax(20px, 1fr)) auto' }}
        role="img"
        aria-label="Tickets criados por dia da semana e hora"
      >
        {/* Cabeçalho: horas */}
        <span />
        {Array.from({ length: 24 }, (_, h) => (
          <span
            key={h}
            className="pb-1 text-center text-[10px] text-muted-foreground tabular-nums"
          >
            {h}
          </span>
        ))}
        <span className="pb-1 pl-2 text-right text-[10px] font-medium text-muted-foreground">
          Total
        </span>

        {data.matrix.map((row, weekday) => {
          const label = WEEKDAY_LABELS[weekday] ?? String(weekday);
          return [
            <span
              key={`w-${weekday}`}
              className="pr-2 text-right text-xs font-medium text-muted-foreground"
            >
              {label}
            </span>,
            ...row.map((count, hour) => (
              <div
                key={`${weekday}-${hour}`}
                title={`${label} ${hour}h — ${count} ${count === 1 ? 'ticket' : 'tickets'}`}
                className="h-6 rounded-[3px]"
                style={{ backgroundColor: cellColor(count, max) }}
              />
            )),
            <span
              key={`t-${weekday}`}
              className="pl-2 text-right text-[13px] font-medium tabular-nums"
            >
              {data.totalsByWeekday[weekday] ?? 0}
            </span>,
          ];
        })}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-4 text-[13px] text-muted-foreground">
        <span>
          Total no período:{' '}
          <span className="font-semibold text-foreground tabular-nums">{data.total}</span>{' '}
          tickets
        </span>
        <span className="ml-auto flex items-center gap-1.5">
          Menos
          {[0.06, 0.3, 0.55, 0.8, 1].map((a) => (
            <span
              key={a}
              className="size-3 rounded-[3px]"
              style={{ backgroundColor: `rgba(12, 196, 182, ${a})` }}
              aria-hidden
            />
          ))}
          Mais
        </span>
      </div>
    </div>
  );
}

export function peaksCsv(data: PeaksReport): CsvTable {
  return {
    headers: ['Dia', ...Array.from({ length: 24 }, (_, h) => `${h}h`), 'Total'],
    rows: data.matrix.map((row, weekday) => [
      WEEKDAY_LABELS[weekday] ?? String(weekday),
      ...row,
      data.totalsByWeekday[weekday] ?? 0,
    ]),
  };
}
